/**
 * Pure structure search logic — no store dependency.
 * The store action is a thin wrapper that reads state slices, calls this,
 * then sets searchResults.
 */

import { Structure, MapClaimSummary } from '../../types';
import { joinClaim } from './shardLoader';

const RESULT_CAP = 10;

export interface SearchStructuresArgs {
  /** Already-trimmed, lowercase query string. */
  lower: string;
  /** Live user-added structures (searched first). */
  userAddedStructures: Structure[];
  /** Shard map keyed by `${areaId}:${shardId}`. */
  structuresByShard: Record<string, Structure[]>;
  /** Active claim summaries keyed by structureId. */
  claimsByStructureId: Record<string, MapClaimSummary>;
  /** When set, only shards with this area prefix are searched. */
  areaId?: string;
  /** Max results to return (default 10). */
  cap?: number;
}

/**
 * Match rules (preserved verbatim from the original store action):
 *   - address starts with `lower`, OR
 *   - address contains ` ${lower}` (word-boundary prefix)
 *
 * User-added structures are searched first so a freshly added property
 * surfaces immediately. Results are de-duped by id and capped at `cap`.
 */
export function searchStructuresPure(args: SearchStructuresArgs): Structure[] {
  const {
    lower,
    userAddedStructures,
    structuresByShard,
    claimsByStructureId,
    areaId,
    cap = RESULT_CAP,
  } = args;

  const matches: Structure[] = [];
  const seen = new Set<string>();
  const areaPrefix = areaId ? `${areaId}:` : null;

  // User-added structures live in a separate live cache; search them
  // first so a freshly added property surfaces immediately.
  for (let i = 0; i < userAddedStructures.length; i++) {
    const s = userAddedStructures[i];
    const address = s.address?.toLowerCase() ?? '';
    if (address.startsWith(lower) || address.includes(` ${lower}`)) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      matches.push(joinClaim(s, claimsByStructureId[s.id]));
      if (matches.length >= cap) return matches;
    }
  }

  outer: for (const key in structuresByShard) {
    if (areaPrefix && !key.startsWith(areaPrefix)) continue;
    const arr = structuresByShard[key];
    for (let i = 0; i < arr.length; i++) {
      const s = arr[i];
      if (seen.has(s.id)) continue;
      const address = s.address?.toLowerCase() ?? '';
      if (address.startsWith(lower) || address.includes(` ${lower}`)) {
        seen.add(s.id);
        matches.push(joinClaim(s, claimsByStructureId[s.id]));
        if (matches.length >= cap) break outer;
      }
    }
  }

  return matches;
}
